import { useState, useEffect, useCallback } from 'react';
import Seo from '../../components/Seo';
import {
  PageLoader,
  SkeletonGrid,
  ErrorState,
  EmptyState,
  Modal,
  Field,
  Pagination,
  Spinner,
} from '../../components/ui';
import { mediaApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { kindForFile } from '../../lib/media';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../lib/format';

const PER_PAGE = 24;

const KIND_TABS = [
  { key: '', label: 'All' },
  { key: 'image', label: 'Images' },
  { key: 'document', label: 'Documents' },
  { key: 'video', label: 'Videos' },
];

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'hidden', label: 'Hidden' },
];

const KIND_LABEL = { image: 'Image', document: 'Document', video: 'Video' };

/** Friendly labels for the /usage reference `type` values. */
const REF_LABEL = {
  news: 'News',
  event: 'Event',
  story: 'Story',
  activity: 'Activity',
  video: 'Video',
  member: 'Member',
  event_recap: 'Event recap',
};

function formatBytes(bytes) {
  if (bytes == null) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const val = bytes / 1024 ** i;
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function kindIcon(kind) {
  if (kind === 'video') return '🎬';
  return '📄';
}

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for non-secure contexts.
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

function StatusPill({ published }) {
  return (
    <span
      className={`pill ${published ? 'bg-green-100 text-green-800' : 'bg-surface-gray text-ink-soft'}`}
    >
      {published ? 'Published' : 'Hidden'}
    </span>
  );
}

export default function AdminMedia() {
  const toast = useToast();
  const [kind, setKind] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ loading: true, error: null, items: [], meta: null });
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [preview, setPreview] = useState(null); // MediaFileResource
  const [edit, setEdit] = useState(null); // { id, name, alt_text, caption }
  const [editErrors, setEditErrors] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const [del, setDel] = useState(null); // { media, usage, loadingUsage, busy }

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    mediaApi
      .list({ kind: kind || undefined, page, per_page: PER_PAGE })
      .then((res) => setState({ loading: false, error: null, items: res.items, meta: res.meta }))
      .catch((error) => setState({ loading: false, error, items: [], meta: null }));
  }, [kind, page]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kindForFile(file)); // image | document | video
      fd.append('alt_text', file.name);
      await mediaApi.upload(fd);
      toast.success('File uploaded.');
      setPage(1);
      load();
    } catch (err) {
      toast.error(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function toggleVisibility(media) {
    setBusyId(media.id);
    try {
      if (media.is_published) {
        await mediaApi.unpublish(media.id);
        toast.success('File hidden.');
      } else {
        await mediaApi.publish(media.id);
        toast.success('File published.');
      }
      load();
    } catch (err) {
      toast.error(err.message || 'Could not update visibility.');
    } finally {
      setBusyId(null);
    }
  }

  async function copyUrl(media) {
    try {
      await copyToClipboard(assetUrl(media.url));
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy link.');
    }
  }

  function openEdit(media) {
    setEdit({
      id: media.id,
      name: media.name || '',
      alt_text: media.alt_text || '',
      caption: media.caption || '',
    });
    setEditErrors({});
  }

  async function saveEdit() {
    if (!edit) return;
    setEditBusy(true);
    setEditErrors({});
    try {
      await mediaApi.update(edit.id, {
        name: edit.name.trim(),
        alt_text: edit.alt_text.trim() || null,
        caption: edit.caption.trim() || null,
      });
      toast.success('Details saved.');
      setEdit(null);
      load();
    } catch (err) {
      setEditErrors(err.errors || {});
      toast.error(err.message || 'Could not save details.');
    } finally {
      setEditBusy(false);
    }
  }

  function openDelete(media) {
    setDel({ media, usage: null, loadingUsage: true, busy: false });
    mediaApi
      .usage(media.id)
      .then((usage) => setDel((d) => (d && d.media.id === media.id ? { ...d, usage, loadingUsage: false } : d)))
      .catch(() => setDel((d) => (d ? { ...d, usage: { in_use: false, references: [] }, loadingUsage: false } : d)));
  }

  async function confirmDelete() {
    if (!del) return;
    setDel((d) => ({ ...d, busy: true }));
    try {
      await mediaApi.remove(del.media.id);
      toast.success('File deleted.');
      setDel(null);
      if (preview?.id === del.media.id) setPreview(null);
      load();
    } catch (err) {
      toast.error(err.message || 'Could not delete file.');
      setDel((d) => (d ? { ...d, busy: false } : d));
    }
  }

  const q = query.trim().toLowerCase();
  const visible = state.items.filter((m) => {
    if (statusFilter === 'published' && !m.is_published) return false;
    if (statusFilter === 'hidden' && m.is_published) return false;
    if (q && !(m.name || '').toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <Seo title="Media Library" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold">Media Library</h1>
        <label className="btn-primary cursor-pointer">
          {uploading ? 'Uploading…' : '+ Upload'}
          <input
            type="file"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
            aria-label="Upload a file"
          />
        </label>
      </div>

      {/* Controls: search + type filter + status filter */}
      <div className="card space-y-4 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="w-full max-w-sm">
            <label className="sr-only" htmlFor="media-search">
              Search media by name
            </label>
            <input
              id="media-search"
              className="input"
              placeholder="Search by name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by type">
            {KIND_TABS.map((t) => (
              <button
                key={t.key || 'all'}
                type="button"
                onClick={() => {
                  setPage(1);
                  setKind(t.key);
                }}
                className={`pill cursor-pointer transition-colors ${
                  kind === t.key
                    ? 'bg-maroon-700 text-white'
                    : 'bg-surface-gray text-ink-soft hover:bg-cream-300'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by status">
          <span className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Status
          </span>
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setStatusFilter(t.key)}
              className={`pill cursor-pointer transition-colors ${
                statusFilter === t.key
                  ? 'bg-gold-400 text-maroon-900'
                  : 'bg-surface-gray text-ink-soft hover:bg-cream-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {state.loading ? (
        // Loaded grid below is 2 -> 3 -> 4 at `gap-4`; the 3-up default was
        // wrong at every breakpoint (1 column on mobile against the real 2).
        state.items.length ? <PageLoader /> : <SkeletonGrid count={8} columns="2-3-4" gap={4} />
      ) : state.error ? (
        <ErrorState error={state.error} onRetry={load} />
      ) : visible.length === 0 ? (
        <EmptyState
          icon="🖼️"
          title={q || statusFilter !== 'all' ? 'No matching media' : 'No media yet'}
          message={
            q || statusFilter !== 'all'
              ? 'Try a different search or filter.'
              : 'Upload images, documents, and videos to use across the site.'
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {visible.map((m) => {
              const isImage = m.kind === 'image';
              const url = assetUrl(m.url);
              return (
                <div key={m.id} className="card flex flex-col overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setPreview(m)}
                    className="group flex h-32 items-center justify-center bg-surface-gray focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-maroon-400"
                    aria-label={`Preview ${m.name}`}
                  >
                    {isImage ? (
                      <img
                        src={url}
                        alt={m.alt_text || ''}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <span className="text-4xl" aria-hidden="true">
                        {kindIcon(m.kind)}
                      </span>
                    )}
                  </button>

                  <div className="flex flex-1 flex-col gap-2 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 flex-1 truncate text-sm font-semibold text-ink" title={m.name}>
                        {m.name || 'Untitled'}
                      </p>
                      <StatusPill published={m.is_published} />
                    </div>
                    <p className="text-xs text-ink-muted">
                      {KIND_LABEL[m.kind] || m.kind} · {formatBytes(m.size)}
                    </p>

                    <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
                      <button
                        type="button"
                        onClick={() => setPreview(m)}
                        className="btn-ghost px-2.5 py-1 text-xs"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(m)}
                        className="btn-ghost px-2.5 py-1 text-xs"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleVisibility(m)}
                        disabled={busyId === m.id}
                        className="btn-ghost px-2.5 py-1 text-xs"
                      >
                        {busyId === m.id ? '…' : m.is_published ? 'Hide' : 'Show'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openDelete(m)}
                        className="btn-ghost px-2.5 py-1 text-xs text-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <Pagination meta={state.meta} onChange={(p) => setPage(p)} />
        </>
      )}

      {/* Preview modal */}
      <Modal open={!!preview} onClose={() => setPreview(null)} title={preview?.name || 'Preview'}>
        {preview && (
          <div className="space-y-4">
            {preview.kind === 'image' ? (
              <img
                src={assetUrl(preview.url)}
                alt={preview.alt_text || ''}
                className="max-h-80 w-full rounded-lg object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-3 rounded-lg bg-surface-alt py-8">
                <span className="text-5xl" aria-hidden="true">
                  {kindIcon(preview.kind)}
                </span>
                <a
                  href={assetUrl(preview.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline px-4 py-2 text-xs"
                >
                  Open / download
                </a>
              </div>
            )}

            <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
              <dt className="font-medium text-ink-soft">Name</dt>
              <dd className="col-span-2 break-words text-ink">{preview.name}</dd>
              <dt className="font-medium text-ink-soft">Type</dt>
              <dd className="col-span-2 text-ink">
                {KIND_LABEL[preview.kind] || preview.kind} · {preview.mime_type}
              </dd>
              <dt className="font-medium text-ink-soft">Size</dt>
              <dd className="col-span-2 text-ink">{formatBytes(preview.size)}</dd>
              <dt className="font-medium text-ink-soft">Alt text</dt>
              <dd className="col-span-2 text-ink">{preview.alt_text || '—'}</dd>
              <dt className="font-medium text-ink-soft">Caption</dt>
              <dd className="col-span-2 text-ink">{preview.caption || '—'}</dd>
              <dt className="font-medium text-ink-soft">Status</dt>
              <dd className="col-span-2">
                <StatusPill published={preview.is_published} />
              </dd>
              <dt className="font-medium text-ink-soft">Uploaded</dt>
              <dd className="col-span-2 text-ink">{formatDate(preview.created_at) || '—'}</dd>
            </dl>

            <div className="flex flex-wrap justify-end gap-2 border-t border-cream-300 pt-4">
              <button type="button" className="btn-ghost" onClick={() => copyUrl(preview)}>
                Copy URL
              </button>
              <button
                type="button"
                className="btn-outline"
                onClick={() => {
                  const m = preview;
                  setPreview(null);
                  openEdit(m);
                }}
              >
                Edit
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit modal */}
      <Modal
        open={!!edit}
        onClose={() => (editBusy ? null : setEdit(null))}
        title="Edit file details"
        footer={
          <>
            <button type="button" className="btn-ghost" onClick={() => setEdit(null)} disabled={editBusy}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={saveEdit} disabled={editBusy}>
              {editBusy ? <Spinner className="h-4 w-4" /> : 'Save'}
            </button>
          </>
        }
      >
        {edit && (
          <div className="space-y-4">
            <Field label="Name" error={editErrors.name?.[0]}>
              <input
                className="input"
                value={edit.name}
                onChange={(e) => setEdit((f) => ({ ...f, name: e.target.value }))}
              />
            </Field>
            <Field
              label="Alt text"
              hint="Describes the image for screen readers and search engines."
              error={editErrors.alt_text?.[0]}
            >
              <input
                className="input"
                value={edit.alt_text}
                onChange={(e) => setEdit((f) => ({ ...f, alt_text: e.target.value }))}
              />
            </Field>
            <Field label="Caption" error={editErrors.caption?.[0]}>
              <textarea
                className="input"
                rows={3}
                value={edit.caption}
                onChange={(e) => setEdit((f) => ({ ...f, caption: e.target.value }))}
              />
            </Field>
          </div>
        )}
      </Modal>

      {/* Delete modal */}
      <Modal
        open={!!del}
        onClose={() => (del?.busy ? null : setDel(null))}
        title="Delete file?"
        footer={
          <>
            <button type="button" className="btn-ghost" onClick={() => setDel(null)} disabled={del?.busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={confirmDelete}
              disabled={del?.busy || del?.loadingUsage}
            >
              {del?.busy ? 'Deleting…' : 'Delete'}
            </button>
          </>
        }
      >
        {del && (
          <div className="space-y-3 text-sm">
            <p className="text-ink-soft">
              “{del.media.name}” will be permanently removed. This cannot be undone.
            </p>

            {del.loadingUsage ? (
              <div className="flex items-center gap-2 text-ink-muted">
                <Spinner className="h-4 w-4" /> Checking where this file is used…
              </div>
            ) : del.usage?.in_use ? (
              <div className="rounded-lg border border-gold-500 bg-gold-50 p-3">
                <p className="font-semibold text-maroon-900">
                  Warning: this file is still in use.
                </p>
                <p className="mt-1 text-ink-soft">Used in:</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-ink-soft">
                  {del.usage.references.map((r) => (
                    <li key={`${r.type}-${r.id}`}>
                      {REF_LABEL[r.type] || r.type}: “{r.title}”
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-ink-muted">
                  Deleting it may break those items. You can still delete if you are sure.
                </p>
              </div>
            ) : (
              <p className="text-ink-muted">This file is not used anywhere.</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
